/**********************************
*
* API index, for use with Lincsafe Portal API (https://services.lincsafe.com/leda/release-api-docs.html#/)
* Creator: Johan Zandstra
* Owner:   Dolphiq
* Date:    28/01/17
*
**********************************/

const NETWORK_TIMEOUT_MS = 30000

// API Version and settings
const API_STANDARDS_TREE = 'x'

// If you've ever worked with an API you'll know that most are served from either a subdomain or under a prefix.
// A prefix or subdomain is required, but only one.
// Avoid putting a version number as your prefix or subdomain as versioning is handled via the Accept header.
const API_PREFIX = 'leda/api'

const API_HOST = process.env.API_HOST()

// Because we aren't versioning the API in the URI we need to define an Accept header to request a specific version. The header is formatted like so.
// Accept: application/vnd.YOUR_SUBTYPE.v1+json
const API_VERSION = 'v2'

// Your subtype is typically a short name of your application or project, all lowercase.
const API_SUBTYPE = ''

import authEndpoint from './endpoints/auth'
import citEndpoint from './endpoints/cit'
import currencyEndpoint from './endpoints/currency'
import departmentEndpoint from './endpoints/department'
import eventEndpoint from './endpoints/event'
import locationEndpoint from './endpoints/location'
import mixEndpoint from './endpoints/mix'
import organizationEndpoint from './endpoints/organization'
import tillEndpoint from './endpoints/till'
import unitRolesEndpoint from './endpoints/unitroles.js'
import reportEndpoint from './endpoints/report'
import softwaresettingsEndpoint from './endpoints/softwaresettings'
import adminsettingsEndpoint from './endpoints/adminsettings'
import unitEndpoint from './endpoints/unit'
import userEndpoint from './endpoints/user'
import utilityEndpoint from './endpoints/utility'

import router from '../router'
import ElementUI from 'element-ui'
import store from '../vuex/store.js'
import Vue from 'vue'

export default {

  // User object will let us check authentication status
  apiConfig: {
    apiStandardsTree: API_STANDARDS_TREE,
    apiPrefix: API_PREFIX,
    apiDomain: API_HOST,
    apiVersion: API_VERSION,
    apiSubtype: API_SUBTYPE,
    callCounter: 0,
    networkTimeout: NETWORK_TIMEOUT_MS
  },
  apiUrl () {
    return API_HOST + '/' + API_PREFIX + '/'
  },
  init () {
  },
  install (Vue, options) {
    // create binding to Vue instance(es)
    this.init()
    var self = this

    // bind Vue instance to local _vue variable
    this._vue = Vue

    Vue.prototype.$dataApi = function (options = {}) {
      return self
    }
  },
  getJwtToken () {
    let idToken = window.localStorage.getItem('id_token')
    if (!idToken) {
      const vegaData = window.localStorage.getItem('vega')
      if (vegaData) {
        const { jwt_token: jwtToken } = JSON.parse(vegaData)
        idToken = jwtToken
      }
    }
    return idToken
  },
  getRefreshToken () {
    let refreshToken = window.localStorage.getItem('id_refresh_token')
    if (!refreshToken) {
      const vegaData = window.localStorage.getItem('vega')
      if (vegaData) {
        const { refresh_token: idRefreshToken } = JSON.parse(vegaData)
        refreshToken = idRefreshToken
      }
    }
    return refreshToken
  },
  request (options) {
    // default headers
    var fullApiUrl = this.apiUrl() + options.url

    let requestTimeout = NETWORK_TIMEOUT_MS
    let url = options.url
    if ((url.indexOf('ui/report/') > -1) && url !== 'ui/report/reports-list') {
      requestTimeout = 10 * 60 * 1000
    }

    // Check for internet connection
    if (!window.navigator.onLine) {
      ElementUI.Message.error('It seems you have lost the internet connection!')
    }

    const vm = this // view-model object for accessing inside inner functions ('this' is ambiguous)
    const token = this.getJwtToken()
    const refreshToken = this.getRefreshToken()

    return new Promise((resolve, reject) => {
      // remember whether this request started with a token or not. If it started without
      // a token (pre-login), we should avoid
      // triggering global refresh/redirect logic for its 401/403 failures.
      var originatedWithToken = !!token
      // set a network timeout
      var networkTimeout = setTimeout(() => {
        console.log('Connection timeout to REST Api')
        var err = {
          status: 500,
          statusMessage: 'Could not connect to backend server'
        }
        // reject Promise
        reject(err)
      }, requestTimeout)

      var requestHeaders = {}

      if (options.headers !== undefined) {
        requestHeaders = options.headers
      }

      requestHeaders.Accept = 'application/json'

      if (options.accept !== undefined) {
        requestHeaders.Accept = options.accept
      }

      if (options.authorization !== false) {
        requestHeaders['Authorization'] = 'Bearer ' + token
      }

      var requestOptions = {
        timeout: requestTimeout,
        headers: requestHeaders,
        credentials: false,
        params: options.params
      }

      if (options.responseType) {
        requestOptions.responseType = options.responseType
      }

      var param1 = null
      var param2 = null

      if (options.method === 'post' || options.method === 'put') {
        param1 = options.body
        param2 = requestOptions
      } else {
        param1 = requestOptions
      }

      this._vue.http[options.method](fullApiUrl, param1, param2).then((response) => {
        // success callback
        clearTimeout(networkTimeout)

        var newResponse = response

        if (response.status === 200) {
          if (typeof options.parseResponse === 'function') {
            var r = options.parseResponse(response)
            if (r === false) {
              newResponse.status = 400 // parse error
              newResponse.statusMessage = 'Parse error' // parse error
              reject(newResponse)
            }
          }
          // default only the data array
          resolve(newResponse)
        } else {
          console.log('reject response != status 200')
          newResponse.status = response.status
          newResponse.statusMessage = 'Status not 200'

          reject(newResponse)
        }
      }, (err) => {
        function handle400 (exc) {
          clearTimeout(networkTimeout)
          let msgCode = 'xxxxxx' // In this scenario (error with 'xxxxxx' code), BE returns the entire message without a translation key.

          if (exc.body.code && exc.body.code === msgCode && exc.body.text) {
            ElementUI.Message.warning(exc.body.text)
          } else if (exc.body.code && exc.body.msgId) {
            // TODO :: in some case backed returns msgId not like a key (SELECT_A_UNIT_FOR_THE_EMPLOYEE)
            // but like string for display (Select a unit for the employee!).
            // This should be changed on the backend and remove this if
            if (exc.body.msgId.includes(' ')) {
              let parsed = exc.body.msgId
              // first regex replace blank space with _ and the second one removes special characters in order to make a key
              parsed = parsed.replace(/\s/g, '_').replace(/[^\w\s]/gi, '').toUpperCase()
              ElementUI.Message.warning(Vue.t('server_messages.' + parsed))
            } else {
              if (exc.body.businessDetails) {
                ElementUI.Message.warning(Vue.t('server_messages.' + exc.body.msgId, { reason: exc.body.businessDetails }))
              } else {
                let message = Vue.t('server_messages.' + exc.body.msgId)
                // Remove the [{reason}] part from the message only if businessDetails is not present
                if (message.includes('[{reason}]')) {
                  message = message.replace(/\s*\[\{reason\}\]/, '')
                }
                ElementUI.Message.warning(message)
              }
            }
          } else if (exc.body.data) {
            ElementUI.Message.warning(exc.body.data)
          } else if (exc.body.message && Array.isArray(exc.body.message) && exc.body.message.length > 0) {
            ElementUI.Message.warning(Vue.t('server_messages.' + exc.body.message[0]))
          } else {
            ElementUI.Message.warning('An error occurred')
          }

          reject(exc)
        }

        function handle401 (exc) {
          clearTimeout(networkTimeout)
          // error callback
          console.log('in error!', err)

          // Handle MFA required error
          if (exc.body && exc.body.msgId === 'MFA_REQUIRED') {
            reject(err)
          }
          if (exc.body.code && exc.body.msgId) {
            ElementUI.Message.warning(Vue.t('server_messages.' + exc.body.msgId))

            if (exc.body.msgId === 'sessionTimeout' && router.currentRoute.path !== '/login') {
              window.localStorage.removeItem('vega')
              window.localStorage.removeItem('id_token')
              window.localStorage.removeItem('id_token_created_at')
              window.localStorage.removeItem('id_refresh_token')

              window.location.href = '/login'
            }
          } else if (exc.body.data) {
            ElementUI.Message.warning(exc.body.data)
          } else {
            // unauthorized error
            // if route is defined, go to login screen!
            console.log('not authorized', router, this)
            store.commit('generalSession/sessionError')
            router.replace({
              name: 'login'
            })

            reject(err)
          }
        }

        function handle404 (exc) {
          clearTimeout(networkTimeout)
          // error callback
          console.log('in error!', err)

          if (exc.body.code && exc.body.msgId) {
            ElementUI.Message.warning(Vue.t('server_messages.' + exc.body.msgId))
          } else if (exc.body.data) {
            ElementUI.Message.warning(exc.body.data)
          } else {
            router.push({
              name: 'generic.error.ErrorPage',
              params: {
                error_response: {
                  error_message: 'unknown_error'
                }
              }
            })
            reject(exc)
          }
        }

        function handle409 (exc) {
          clearTimeout(networkTimeout)
          // error callback
          console.log('in error!', err)

          if (exc.body.code && exc.body.msgId) {
            ElementUI.Message.warning(Vue.t('server_messages.' + exc.body.msgId))
          } else if (exc.body.data) {
            ElementUI.Message.warning(exc.body.data)
          } else {
            router.push({
              name: 'generic.error.ErrorPage',
              params: {
                error_response: {
                  error_message: 'unknown_error'
                }
              }
            })
          }
          reject(exc)
        }

        function handleOthers (exc) {
          clearTimeout(networkTimeout)
          // error callback
          console.log('in error!', err)

          router.replace({
            name: 'generic.error.ErrorPage',
            params: {
              error_response: exc
            }
          })
          // put back error to program script
          reject(exc)
        }

        if (err.status === 400) {
          handle400(err)
        } else if (err.status === 401 || err.status === 403) {
          // to prevent random redirect to login page in case he has error before even logging in
          // this is the case if user initially goes to /unit or some in app route before login
          // it can be caused by bookmark to a app page or direct link
          if (!originatedWithToken) {
            reject(err)
            return
          }
          // for now just for logout, but can be used for other endpoints in the future if needed
          var skipRefresh = options && options.skipRefresh === true

          // If session timed out on logout, clear session immediately and reject.
          if (skipRefresh && err.body && (err.body.msgId === 'sessionTimeout' || err.body.msgId === 'accessNotAllowed')) {
            try {
              // Use silent clear to avoid showing an "Unauthorized" warning
              store.commit('generalSession/clearSessionSilent')
            } catch (error) {
              console.warn('Could not commit clearSessionSilent', error)
            }
            window.localStorage.removeItem('vega')
            window.localStorage.removeItem('id_token')
            window.localStorage.removeItem('id_token_created_at')
            window.localStorage.removeItem('id_refresh_token')
            reject(err)
            return
          }

          if (err.body.msgId === 'sessionTimeout' && router.currentRoute.path !== '/login') {
            vm._vue.http.post(this.apiUrl() + 'ui/auth/refresh', {
              refreshToken: refreshToken
            }, {
              timeout: NETWORK_TIMEOUT_MS
            }).then(response => {
              clearTimeout(networkTimeout) // recall will create a new timeout object. So we should clean the old one
              const [token, refreshToken] = response.body.message
              const authData = {
                jwt_token: token,
                refresh_token: refreshToken,
                createdAt: (new Date()).getTime()
              }
              window.localStorage.setItem('vega', JSON.stringify(authData))
              window.localStorage.setItem('id_token', token)
              window.localStorage.setItem('id_refresh_token', refreshToken)
              window.localStorage.setItem('id_token_created_at', (new Date()).getTime())

              // lets try the request with new tokens
              resolve(vm.request(options))
            }, err2 => {
              if (err2.status === 400) {
                handle400(err2)
              } else if (err2.status === 401 || err2.status === 403) {
                console.log('Apparently token expired.')
                handle401(err2)
              } else if (err2.status === 404) {
                handle404(err2)
              } else if (err2.status === 409) {
                handle409(err2)
              } else {
                handleOthers(err2)
              }
            })
          } else if (err.body.msgId === 'accessNotAllowed' && router.currentRoute.path !== '/login') {
            ElementUI.Message.warning(Vue.t('server_messages.' + err.body.msgId))
            // ensure Vuex session is cleared so UI updates (hide header/sidemenu)
            window.localStorage.removeItem('vega')
            window.localStorage.removeItem('id_token')
            window.localStorage.removeItem('id_token_created_at')
            window.localStorage.removeItem('id_refresh_token')
            window.location.reload(true)
          } else if (err.body.msgId === 'accessNotAllowed' && router.currentRoute.path.includes('/login')) {
            window.localStorage.removeItem('vega')
            window.localStorage.removeItem('id_token')
            window.localStorage.removeItem('id_token_created_at')
            window.localStorage.removeItem('id_refresh_token')
          } else {
            handle401(err)
          }
        } else if (err.status === 404) {
          handle404(err)
        } else if (err.status === 409) {
          handle409(err)
        } else {
          handleOthers(err)
        }
      }).catch(function (error) {
        console.log('Catch error', error)
        reject(error)
      })
    })
  },
  endpoints: {
    // call endpoint
    auth: authEndpoint,
    cit: citEndpoint,
    currency: currencyEndpoint,
    department: departmentEndpoint,
    event: eventEndpoint,
    location: locationEndpoint,
    mix: mixEndpoint,
    organization: organizationEndpoint,
    till: tillEndpoint,
    unitRoles: unitRolesEndpoint,
    report: reportEndpoint,
    softwaresettings: softwaresettingsEndpoint,
    adminsettingsEndpoint: adminsettingsEndpoint,
    unit: unitEndpoint,
    user: userEndpoint,
    utility: utilityEndpoint
  }
}



// WEBPACK FOOTER //
// ./src/api/index.js